// Phase 1A Phase 9A.4 — useSignalWatchlist hook.
//
// Read-only consumer of signal_watchlist filtered to active rows for
// the caller (removed_at IS NULL), plus the toggle_signal_watchlist
// RPC for atomic add / soft-remove / re-arm. RLS scopes reads to
// caller via signal_watchlist_select_own; the RPC is SECURITY
// DEFINER and validates auth.uid() server-side.
//
// ⚠️ KNOWN SCHEMA MISALIGNMENT (2026-05-12):
//   The deployed `signal_watchlist` table (per migration
//   20260508000109_signal_cards.sql) has columns
//   (id, user_id, signal_id, notes, created_at) — NO fixture_id, NO
//   removed_at. This hook was written against the EARLIER
//   20260425000034_signal_watchlist_foundation.sql schema with
//   fixture_id + removed_at, which is NOT what's in production.
//   The .from("signal_watchlist").select("fixture_id") query
//   therefore returns PostgREST 400 (column not found) and pollutes
//   the console.
//
//   PRAGMATIC FIX (tonight): swallow the 400, return empty set, log
//   once. The watchlist UI degrades to "nothing watched" cleanly
//   instead of error-spamming. A proper fix needs a schema decision
//   (resurrect the fixture_id watchlist OR refactor consumers to
//   work off signal_id) — tracked separately.
//
// Trust contract:
//   - No localStorage / sessionStorage (CLAUDE.md §3).
//   - No external API. No randomness. No clock-derived numerics.
//   - Soft-fails on either read or RPC error: surfaces error string
//     for the toggle path, silently returns empty set on read.
//   - Idempotency: RPC is server-atomic (`select … for update`); the
//     hook also debounces concurrent toggles via `togglingFixtureId`.
//   - Refetch after every successful toggle so the in-memory Set
//     mirrors the persisted state.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface UseSignalWatchlistState {
  watchedFixtureIds: ReadonlySet<string>;
  loading: boolean;
  error: string | null;
  togglingFixtureId: string | null;
  isWatched: (fixtureId: string) => boolean;
  toggleWatch: (fixtureId: string) => Promise<{ ok: boolean; watching?: boolean }>;
  refetch: () => Promise<void>;
}

const EMPTY_SET: ReadonlySet<string> = new Set<string>();

interface ToggleResponse {
  fixture_id?: string;
  watching?: boolean;
  mode?: string;
}

export function useSignalWatchlist(): UseSignalWatchlistState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [watched, setWatched] = useState<ReadonlySet<string>>(EMPTY_SET);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    // Tonight's hotfix: short-circuit to empty set without making
    // the request. The deployed `signal_watchlist` table has columns
    // (id, user_id, signal_id, notes, created_at) — NOT the
    // (fixture_id, removed_at, …) shape this hook was written for.
    // Making the request returns PostgREST 400 and pollutes the
    // browser network tab + console on every page that mounts this
    // hook (Home, Watchlist, Exchange).
    //
    // The watchlist feature is therefore non-functional and degrades
    // to "nothing watched" cleanly. Tracked for schema realignment:
    // see top-of-file comment + migration files
    //   20260425000034_signal_watchlist_foundation.sql (intended)
    //   20260508000109_signal_cards.sql (deployed)
    // Reference: useSignalWatchlist.ts (this file) line 86 + the
    // toggle RPC also targets non-existent columns.
    //
    // When the schema is realigned, restore the original implementation:
    //   const r = await supabase.from("signal_watchlist")
    //     .select("fixture_id").is("removed_at", null);
    //   …
    //
    // Suppress the `supabase` warning since it's no longer referenced
    // in the short-circuit path. eslint disabled for the unused-binding.
    void supabase;
    if (!user) {
      setWatched(EMPTY_SET);
      setError(null);
      setLoading(false);
      return;
    }
    setWatched(EMPTY_SET);
    setError(null);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const toggleWatch = useCallback(
    async (fixtureId: string): Promise<{ ok: boolean; watching?: boolean }> => {
      if (!user) return { ok: false };
      if (toggling !== null) return { ok: false };
      if (typeof fixtureId !== "string" || fixtureId.length === 0) return { ok: false };
      setToggling(fixtureId);
      setError(null);
      const r = await supabase.rpc("toggle_signal_watchlist", { p_fixture_id: fixtureId });
      if (r.error || r.data === null || typeof r.data !== "object") {
        setError(r.error?.message ?? "toggle_signal_watchlist: empty response");
        setToggling(null);
        return { ok: false };
      }
      const data = r.data as unknown as ToggleResponse;
      const watching = data.watching === true;
      // Optimistic local update (defense-in-depth; refetch confirms).
      setWatched((prev) => {
        const next = new Set(prev);
        if (watching) next.add(fixtureId);
        else next.delete(fixtureId);
        return next;
      });
      await refetch();
      setToggling(null);
      return { ok: true, watching };
    },
    [supabase, user, toggling, refetch],
  );

  const isWatched = useCallback((fixtureId: string): boolean => watched.has(fixtureId), [watched]);

  return useMemo<UseSignalWatchlistState>(
    () => ({
      watchedFixtureIds: watched,
      loading,
      error,
      togglingFixtureId: toggling,
      isWatched,
      toggleWatch,
      refetch,
    }),
    [watched, loading, error, toggling, isWatched, toggleWatch, refetch],
  );
}
